import { ElMessage } from 'element-plus'

const TOKEN_KEY = 'mall_token'
const REFRESH_TOKEN_KEY = 'mall_refresh_token'
const USER_KEY = 'mall_user'

// Token相关操作
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY)
}

export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token)
}

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY)
}

// RefreshToken相关操作
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export const setRefreshToken = (token) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export const removeRefreshToken = () => {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

// 用户信息相关操作
export const getUser = () => {
  try {
    const userStr = localStorage.getItem(USER_KEY)
    return userStr ? JSON.parse(userStr) : null
  } catch (error) {
    console.error('获取用户信息失败:', error)
    return null
  }
}

export const setUser = (user) => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch (error) {
    console.error('保存用户信息失败:', error)
  }
}

export const removeUser = () => {
  localStorage.removeItem(USER_KEY)
}

/** 动作别名：与后端 PermissionUtils 保持一致 */
const ACTION_ALIASES = {
  create: 'add',
  add: 'create',
  update: 'edit',
  edit: 'update',
  delete: 'remove',
  remove: 'delete'
}

/**
 * 展开权限等价形式（动作别名 + 小写）
 * @param {string} permission
 * @returns {string[]}
 */
export function expandPermissionAliases(permission) {
  if (!permission) return []
  const set = new Set([permission])
  const idx = permission.lastIndexOf(':')
  if (idx > 0) {
    const prefix = permission.slice(0, idx + 1)
    const action = permission.slice(idx + 1)
    if (ACTION_ALIASES[action]) {
      set.add(prefix + ACTION_ALIASES[action])
    }
  }
  for (const s of [...set]) {
    set.add(s.toLowerCase())
  }
  return [...set]
}

/**
 * 用户权限是否覆盖所需权限（含别名）
 * @param {string[]} userPerms
 * @param {string} permission
 */
function matchPermission(userPerms, permission) {
  if (!userPerms || !permission) return false
  if (userPerms.includes('*:*:*')) return true
  const needed = expandPermissionAliases(permission)
  const lowerUser = userPerms.map((p) => (p || '').toLowerCase())
  return needed.some((n) => userPerms.includes(n) || lowerUser.includes(n.toLowerCase()))
}

// 权限判断
export const hasPermission = (permission) => {
  const user = getUser()
  if (!user || !user.permissions) return false
  return matchPermission(user.permissions, permission)
}

export const hasAnyPermission = (permissions) => {
  if (!Array.isArray(permissions)) return hasPermission(permissions)

  const user = getUser()
  if (!user || !user.permissions) return false
  if (user.permissions.includes('*:*:*')) return true

  return permissions.some((permission) => matchPermission(user.permissions, permission))
}

export const hasAllPermissions = (permissions) => {
  if (!Array.isArray(permissions)) return hasPermission(permissions)

  const user = getUser()
  if (!user || !user.permissions) return false
  if (user.permissions.includes('*:*:*')) return true

  return permissions.every((permission) => matchPermission(user.permissions, permission))
}

// 角色判断
export const hasRole = (role) => {
  const user = getUser()
  if (!user || !user.roles) return false

  // 超级管理员拥有所有角色
  if (user.roles.includes('admin')) return true

  return user.roles.includes(role)
}

export const hasAnyRole = (roles) => {
  if (!Array.isArray(roles)) return hasRole(roles)

  const user = getUser()
  if (!user || !user.roles) return false

  // 超级管理员拥有所有角色
  if (user.roles.includes('admin')) return true

  return roles.some(role => user.roles.includes(role))
}

export const hasAllRoles = (roles) => {
  if (!Array.isArray(roles)) return hasRole(roles)

  const user = getUser()
  if (!user || !user.roles) return false

  // 超级管理员拥有所有角色
  if (user.roles.includes('admin')) return true

  return roles.every(role => user.roles.includes(role))
}

// 检查是否已登录
export const isAuthenticated = () => {
  const token = getToken()
  const user = getUser()
  return !!(token && user)
}

// 清除认证信息
export const clearAuth = () => {
  removeToken()
  removeRefreshToken()
  removeUser()
}

// Token有效性检查
export const isTokenValid = () => {
  const token = getToken()
  if (!token) return false

  try {
    // 简单的JWT解析，实际项目中应该验证签名和过期时间
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Date.now() / 1000

    // 检查token是否过期（提前5分钟过期）
    return payload.exp > (now + 300)
  } catch (error) {
    console.error('Token验证失败:', error)
    return false
  }
}

// 刷新Token
export const refreshToken = async () => {
  const token = getToken()
  if (!token) return false

  try {
    // 这里应该调用后端的刷新token接口
    // const response = await fetch('/api/auth/refresh', {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': `Bearer ${token}`,
    //     'Content-Type': 'application/json'
    //   }
    // })

    // if (response.ok) {
    //   const data = await response.json()
    //   setToken(data.token)
    //   return true
    // }

    // 临时返回false，需要实现实际的刷新逻辑
    ElMessage.warning('Token即将过期，请重新登录')
    return false
  } catch (error) {
    console.error('刷新Token失败:', error)
    return false
  }
}

// 登录
export const login = async (loginForm) => {
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(loginForm)
    })

    if (!response.ok) {
      throw new Error('登录失败')
    }

    const data = await response.json()

    if (data.code === 200) {
      const { token, user } = data.data

      // 保存token和用户信息
      setToken(token)
      setUser(user)

      ElMessage.success('登录成功')
      return { success: true, data: user }
    } else {
      ElMessage.error(data.message || '登录失败')
      return { success: false, message: data.message }
    }
  } catch (error) {
    console.error('登录错误:', error)
    ElMessage.error('登录失败，请检查网络连接')
    return { success: false, message: '网络连接失败' }
  }
}

// 登出
export const logout = async () => {
  try {
    const token = getToken()
    if (token) {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
    }
  } catch (error) {
    console.error('登出请求失败:', error)
  } finally {
    // 无论请求成功与否都清除本地认证信息
    clearAuth()
    ElMessage.success('已退出登录')
  }
}

// 获取用户信息
export const getUserInfo = async () => {
  try {
    const token = getToken()
    if (!token) {
      throw new Error('未登录')
    }

    const response = await fetch('/api/auth/userinfo', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('获取用户信息失败')
    }

    const data = await response.json()

    if (data.code === 200) {
      const userInfo = data.data
      setUser(userInfo)
      return { success: true, data: userInfo }
    } else {
      ElMessage.error(data.message || '获取用户信息失败')
      return { success: false, message: data.message }
    }
  } catch (error) {
    console.error('获取用户信息错误:', error)
    ElMessage.error('获取用户信息失败')
    return { success: false, message: '网络连接失败' }
  }
}

// 修改密码
export const changePassword = async (passwordData) => {
  try {
    const token = getToken()
    if (!token) {
      throw new Error('未登录')
    }

    const response = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(passwordData)
    })

    const data = await response.json()

    if (data.code === 200) {
      ElMessage.success('密码修改成功')
      return { success: true }
    } else {
      ElMessage.error(data.message || '密码修改失败')
      return { success: false, message: data.message }
    }
  } catch (error) {
    console.error('修改密码错误:', error)
    ElMessage.error('密码修改失败')
    return { success: false, message: '网络连接失败' }
  }
}

// 导出默认对象
export default {
  getToken,
  setToken,
  removeToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken,
  getUser,
  setUser,
  removeUser,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  hasAllRoles,
  isAuthenticated,
  clearAuth,
  isTokenValid,
  refreshToken,
  login,
  logout,
  getUserInfo,
  changePassword
}